import styled from '@emotion/styled';
import Image from 'next/image';
import { Lato } from 'next/font/google';

import {
  getSlotItemTheme,
  isSubjectMastered,
  getMasteryIcons,
  getDisplaySubjectLabels,
} from './slotRingUtils';
import { type SubjectMetrics, type StudentScheduleSlot } from '../../model';
import { isCurrentTimeInRange, isTimePast } from '../utils';
import { fadeInAnimation } from '../../animations';
import { LockUnlock } from '../LockUnlock';
import { Box } from '@mui/material';

const ICON_SIZE = 16;
const lato = Lato({ weight: ['400', '700'], subsets: [], display: 'swap' });

const MainWrapper = styled.div<{ $index: number }>`
  position: absolute;
  left: 50%;
  bottom: 50%;
  transform: translate(50%, 50%);
  width: 0;
  height: 0;
  opacity: 0;
  animation-name: ${fadeInAnimation};
  animation-delay: ${({ $index }) => 700 + $index * 120}ms;
  animation-fill-mode: forwards;
  animation-duration: 120ms;
  animation-timing-function: ease-out;
`;

const Container = styled.div<{ $bgColor: string; $textColor: string; $disabled: boolean }>`
  width: 440px;
  height: 440px;
  margin-top: -440px;
  background-color: ${({ $bgColor }) => $bgColor};
  color: ${({ $textColor }) => $textColor};
  cursor: ${({ $disabled }) => ($disabled ? 'auto' : 'pointer')};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  transform-origin: 0 100%;
  border: 2px solid transparent;
  &:hover {
    background-color: #b7ccff;
  }
`;

const InnerContainer = styled.div`
  position: absolute;
  bottom: 0;
  font-size: 14px;
  display: inline-block;
  text-align: center;
  font-weight: 500;
  user-select: none;
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
`;

const IconsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
`;

type Props = {
  data: StudentScheduleSlot;
  angle: number;
  skew: number;
  currentTime: Date;
  index: number;
  onClick: (data: StudentScheduleSlot) => void;
  stats: SubjectMetrics | undefined;
  timeSpentStats: [number, number];
};

export const SlotItem = ({
  data,
  angle,
  skew,
  currentTime,
  index,
  onClick,
  stats,
  timeSpentStats,
}: Props) => {
  const { interactive, repeatedClass, startTime, endTime } = data;
  const timeSpent = repeatedClass ? timeSpentStats[1] : timeSpentStats[0];

  const isCurrentlyActive = isCurrentTimeInRange(currentTime, startTime, endTime);
  const isComplete = isTimePast(currentTime, endTime);
  const isPastSlot = isTimePast(currentTime, startTime);
  const isMastered = isSubjectMastered(stats);
  const { iconsVisible, bgColor, textColor } = getSlotItemTheme(
    data,
    isMastered,
    isCurrentlyActive,
    isComplete
  );
  const { accuracyIcon, masteryIcon, timeSpentIcon } = getMasteryIcons(stats, timeSpent);

  const lockIconVisible = repeatedClass;
  const statsIconVisible = !lockIconVisible && iconsVisible;
  const showUnlockIcon = !!repeatedClass && isMastered;

  const isFirstItem = index === 0;
  const _skew = isFirstItem ? skew : skew + 1;

  const handleClick = () => {
    onClick(data);
  };

  return (
    <MainWrapper $index={index}>
      <Container
        onClick={handleClick}
        $bgColor={bgColor}
        $textColor={textColor}
        $disabled={!interactive || !isPastSlot}
        style={{ transform: `rotate(${angle}deg) skew(-${_skew}deg)` }}
      >
        <InnerContainer
          style={{
            transform: `skew(${_skew}deg) rotate(${81}deg)`,
            width: '95px',
            paddingRight: '20px',
            left: isFirstItem ? '46%' : '44%',
            overflow: 'hidden',
          }}
        >
          <Label className={lato.className}>
            {getDisplaySubjectLabels(data.slotLabel, showUnlockIcon)}
          </Label>
          <IconsContainer
            style={{
              visibility: statsIconVisible ? 'visible' : 'hidden',
            }}
          >
            <Image src={masteryIcon} alt="" width={ICON_SIZE} height={ICON_SIZE} />
            <Image src={accuracyIcon} alt="" width={ICON_SIZE} height={ICON_SIZE} />
            <Image src={timeSpentIcon} alt="" width={ICON_SIZE} height={ICON_SIZE} />
            <Box sx={{ position: 'absolute', visibility: lockIconVisible ? 'visible' : 'hidden' }}>
              <LockUnlock lock={!showUnlockIcon} />
            </Box>
          </IconsContainer>
        </InnerContainer>
      </Container>
    </MainWrapper>
  );
};
